import type { Item } from '@morales/shared';

/** The business runs on New Mexico time, whatever the phone's own clock says. */
export const BUSINESS_TZ = 'America/Denver';

function zonedParts(date: Date): Record<string, string> {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: BUSINESS_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  return Object.fromEntries(parts.map((p) => [p.type, p.value]));
}

/** Today's date in the business time zone, YYYY-MM-DD. */
export function todayIso(now: Date = new Date()): string {
  const p = zonedParts(now);
  return `${p.year}-${p.month}-${p.day}`;
}

export function businessHour(now: Date = new Date()): number {
  return Number(zonedParts(now).hour);
}

/** Noon UTC on that calendar day, so formatting with timeZone UTC never slips a day. */
export function isoToDate(iso: string): Date {
  const [y = 1970, m = 1, d = 1] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12));
}

export function isIsoDate(value: string | null | undefined): value is string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  return isoToDate(value).toISOString().slice(0, 10) === value;
}

export function addDays(iso: string, days: number): string {
  const d = isoToDate(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

const DATE_STYLES = {
  long: { weekday: 'long', month: 'long', day: 'numeric' },
  day: { weekday: 'short', month: 'short', day: 'numeric' },
  date: { month: 'short', day: 'numeric', year: 'numeric' },
  /** a birthday: the month and the day, never a weekday (it would be the birth year's) */
  monthDay: { month: 'short', day: 'numeric' },
} satisfies Record<string, Intl.DateTimeFormatOptions>;

export function formatDate(iso: string, intlLocale: string, style: keyof typeof DATE_STYLES = 'day'): string {
  return new Intl.DateTimeFormat(intlLocale, { ...DATE_STYLES[style], timeZone: 'UTC' }).format(isoToDate(iso));
}

/** ISO weekday 1 = Monday ... 7 = Sunday. 2024-01-01 was a Monday. */
export function weekdayName(isoWeekday: number, intlLocale: string): string {
  return new Intl.DateTimeFormat(intlLocale, { weekday: 'short', timeZone: 'UTC' }).format(
    new Date(Date.UTC(2024, 0, isoWeekday, 12)),
  );
}

/** A load's name in the reader's language: a load named after its weekday ("Tuesday") reads "martes" in Spanish. */
export function loadName(l: { weekday: number; name: string }, intlLocale: string): string {
  const day = (locale: string) => new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2024, 0, l.weekday, 12)));
  return l.name.trim().toLowerCase() === day('en-US').toLowerCase() ? day(intlLocale) : l.name;
}

const moneyFormats = new Map<string, Intl.NumberFormat>();
export function formatMoney(amount: number, intlLocale: string): string {
  let f = moneyFormats.get(intlLocale);
  if (!f) {
    f = new Intl.NumberFormat(intlLocale, { style: 'currency', currency: 'USD' });
    moneyFormats.set(intlLocale, f);
  }
  return f.format(amount);
}

/** 2 -> "2", 2.5 -> "2½", 0.5 -> "½"; anything else keeps two decimals. */
export function formatQty(qty: number): string {
  if (Number.isInteger(qty)) return String(qty);
  if (Number.isInteger(qty * 2)) {
    const whole = Math.floor(qty);
    return whole ? `${whole}½` : '½';
  }
  return String(Math.round(qty * 100) / 100);
}

/** Accepts "(505) 555-1234", "505.555.1234", "+1 505 555 1234"; returns E.164 or null. */
export function normalizePhone(input: string): string | null {
  let digits = input.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  return digits.length === 10 ? `+1${digits}` : null;
}

export function formatPhone(e164: string): string {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : e164;
}

export function packLabel(item: Pick<Item, 'packUnit' | 'packDesc'>): string {
  return item.packDesc ? `${item.packUnit} · ${item.packDesc}` : item.packUnit;
}

/** A timestamp as the office clock reads it: date and time in Denver. */
export const whenAt = (iso: string, intlLocale: string): string => {
  const d = new Date(iso);
  return `${formatDate(todayIso(d), intlLocale)} ${new Intl.DateTimeFormat(intlLocale, { timeZone: BUSINESS_TZ, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(d)}`;
};

/**
 * A number as people type it: "12", " 12.5 ", "$1,250", "1,250.00", "2,5" (a Spanish decimal), "1.250,50". A comma
 * followed by groups of exactly three digits is thousands; a single other comma is the decimal point. Anything else
 * is null, so a screen can say so instead of saving the wrong amount. Rounded to 3 decimals.
 */
export function parseTyped(raw: string, opts: { negative?: boolean } = {}): number | null {
  let s = raw.trim().replace(/[$\s]/g, '');
  if (s === '') return null;
  let sign = 1;
  if (s.startsWith('-')) {
    if (!opts.negative) return null;
    sign = -1;
    s = s.slice(1);
  }
  if (/^\d{1,3}(,\d{3})+(\.\d+)?$/.test(s)) s = s.replace(/,/g, ''); // 1,250 or 1,250.50
  else if (/^\d{1,3}(\.\d{3})+,\d+$/.test(s)) s = s.replace(/\./g, '').replace(',', '.'); // 1.250,50
  else if (/^\d*,\d+$/.test(s)) s = s.replace(',', '.'); // 2,5
  if (!/^(\d+(\.\d*)?|\.\d+)$/.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) ? (sign * Math.round(n * 1000)) / 1000 : null;
}

/** A timestamp shown as its day in Denver (a payment at 7 pm is that day, not tomorrow's UTC date). */
export function formatStamp(ts: string, intlLocale: string, style: keyof typeof DATE_STYLES = 'day'): string {
  // a plain date is already a business day
  return formatDate(/^\d{4}-\d{2}-\d{2}$/.test(ts) ? ts : todayIso(new Date(ts)), intlLocale, style);
}

/** Cents, half away from zero, the same as the server (4.725 is 4.73 though the float is 4.72499...). */
export function roundCents(n: number): number {
  if (!Number.isFinite(n)) return n;
  const out = (Math.sign(n) * Math.round(Number(`${Math.abs(n).toFixed(10)}e2`))) / 100;
  return out === 0 ? 0 : out;
}
